import { Button } from '@/ui/atoms/button/Button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/ui/atoms/dropdown/DropdownMenu'
import { Typography } from '@/ui/atoms/typography/Typography'
import { Check, Loader2, SettingsIcon } from 'lucide-react'
import * as hl from '@nktkas/hyperliquid'
import { isTestnet } from '@/config/consts'
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts'
import { useDynamicContext } from '@dynamic-labs/sdk-react-core'
import { isEthereumWallet } from '@dynamic-labs/ethereum'
import { useState } from 'react'
import { captureError } from 'instrumentation-client'
import { BlockSizeError } from '../logic/types'
import { trackEvent } from '@/utils/fathom'
import { zeroAddress } from 'viem'

const transport = new hl.HttpTransport({ isTestnet })

const AGENT_NAME = 'HypurrFiBlocks'

export function SettingsPopOver() {
  const { primaryWallet } = useDynamicContext()
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [success, setSuccess] = useState(false)
  const [bigBlocks, setBigBlocks] = useState(false)

  async function setIsUsingBigBlocks(usingBigBlocks: boolean) {
    try {
      setIsLoading(true)
      setError(false)
      setSuccess(false)

      if (!primaryWallet || !isEthereumWallet(primaryWallet)) {
        throw new Error('No wallet found')
      }

      const walletClient = await primaryWallet.getWalletClient()
      const hlClient = new hl.WalletClient({
        wallet: walletClient,
        transport,
        isTestnet,
      })

      const agentKey = generatePrivateKey()
      const agentAccount = privateKeyToAccount(agentKey)

      await hlClient.approveAgent({
        agentAddress: agentAccount.address,
        agentName: AGENT_NAME,
      })

      const hlAgentClient = new hl.WalletClient({
        wallet: agentAccount,
        transport,
        isTestnet,
      })
      const response = await hlAgentClient.evmUserModify({ usingBigBlocks })

      if (response.status !== 'ok') {
        throw new BlockSizeError('Failed to set block size', { response })
      }

      await hlClient.approveAgent({
        agentAddress: zeroAddress,
        agentName: AGENT_NAME,
      })

      setBigBlocks(usingBigBlocks)
      setSuccess(true)
      trackEvent('set_block_size_success')
    } catch (error) {
      if (error instanceof Error) {
        captureError(error, error instanceof BlockSizeError ? error.details : undefined)

        if (!error.message.includes('User rejected') && !error.message.includes('User disapproved')) {
          trackEvent('set_block_size_error')
          console.error(error)
          setError(true)
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        setError(false)
        setIsLoading(false)
        setSuccess(false)
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          className="h-[30px] w-[30px] rounded-full border-white/10 bg-white/10 p-0 text-white/70"
          prefixIcon={<SettingsIcon size={18} />}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[260] mt-2 border border-white/20 p-0">
        <div className="px-6 py-4 text-sm">
          <Typography variant="p" className="mb-1 font-bold text-sm">
            Set your preferred block size
          </Typography>
          <Typography variant="prompt">
            For more information, see the{' '}
            <a
              href="https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/hyperevm/dual-block-architecture"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              official documentation
            </a>
            .
          </Typography>

          <div className="mt-4 flex items-center gap-2">
            <BlockButton onClick={() => setIsUsingBigBlocks(true)} disabled={isLoading}>
              Big blocks
            </BlockButton>
            <BlockButton onClick={() => setIsUsingBigBlocks(false)} disabled={isLoading}>
              Small blocks
            </BlockButton>
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {success && <Check className="h-4 w-4 text-primary" />}
          </div>
          {error && <p className="mt-2 text-red-300 text-xs">Failed to set block size</p>}
          {success && (
            <Typography variant="prompt" className="mt-2 inline-block">
              Preferences set to use {bigBlocks ? 'big' : 'small'} blocks
            </Typography>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function BlockButton({
  onClick,
  children,
  disabled,
}: { onClick: () => void; children: React.ReactNode; disabled: boolean }) {
  return (
    <Button onClick={onClick} className="w-24 py-0.5 text-xs" size="sm" disabled={disabled}>
      {children}
    </Button>
  )
}
